import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {

  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,

  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalculatorIcon,
  TagIcon,
  BeakerIcon,
 
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { fetchDiagnosticCenterProfile, fetchLabTestBookings } from '../../../services/MasterAdmin/diagnosticCenterService';
import { toggleVendorStatus } from '../../../services/MasterAdmin/vendorService';
import { DiagnosticCenter } from '../../../models/MasterAdmin/diagnosticCenter';
import { LabTestBooking } from '../../../models/MasterAdmin/labTestBooking';
import FilePreview from '../../../components/MasterAdmin/FilePreview';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendValue?: string;
  description?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, trendValue, description }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-2">{value}</p>
        {trend && (
          <p className={`text-sm mt-2 ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {trendValue}
          </p>
        )}
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">{icon}</div>
    </div>
  </div>
);



const DiagnosticCenterVendorDashboard: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [diagnosticCenterData, setDiagnosticCenterData] = useState<DiagnosticCenter | null>(null);
  const [labBookings, setLabBookings] = useState<LabTestBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const loadData = async () => {
      if (!vendorId) return;
      
      try {
        setLoading(true);
        const [profileResponse, bookingsResponse] = await Promise.all([
          fetchDiagnosticCenterProfile(vendorId),
          fetchLabTestBookings(vendorId)
        ]);
        setDiagnosticCenterData(profileResponse.data);
        setLabBookings(bookingsResponse.bookings);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [vendorId]);

  const handleToggleStatus = async (vendorId: string, currentStatus: string) => {
    try {
      setUpdatingStatus(vendorId);
      const response = await toggleVendorStatus(vendorId);
      
      if (response.success) {
        setDiagnosticCenterData((prev: DiagnosticCenter | null) => {
          if (!prev) return null;
          return {
            ...prev,
            vendor: {
              ...prev.vendor,
              status: response.data.status as 'approved' | 'pending' | 'rejected'
            }
          };
        });
      } else {
        setError('Failed to update vendor status. Please try again.');
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status. Please try again later.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const filteredBookings = labBookings.filter(booking => {
    const matchesSearch = 
      booking.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (booking.user.phone_number?.includes(searchQuery) || false) ||
      booking.selectedTests.some(test => test.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || booking.bookingStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const paginatedBookings = filteredBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !diagnosticCenterData) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Diagnostic center data not found'}</p>
      </div>
    );
  }

  const metrics = (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <MetricCard
        title="Total Tests Booked"
        value={labBookings.length}
        icon={<CalendarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />}
        description="All time bookings"
      />
      <MetricCard
        title="Completed Tests"
        value={labBookings.filter(b => b.bookingStatus === 'Completed').length}
        icon={<CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
        description="Successfully completed"
      />
      <MetricCard
        title="Pending Tests"
        value={labBookings.filter(b => b.bookingStatus === 'Pending').length}
        icon={<ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
        description="Awaiting completion"
      />
      <MetricCard
        title="Cancelled Tests"
        value={labBookings.filter(b => b.bookingStatus === 'Cancelled').length}
        icon={<XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />}
        description="Cancelled bookings"
      />
    </div>
  );

  // const todaySchedule = (
  //   <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
  //     <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Today's Schedule</h2>
  //     <div className="space-y-4">
  //       {labBookings
  //         .filter(booking => booking.bookingDate === new Date().toISOString().split('T')[0])
  //         .map((booking) => (
  //           <div key={booking.bookingId} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
  //             <div className="flex justify-between items-start mb-2">
  //               <div>
  //                 <p className="text-sm font-medium text-gray-900 dark:text-white">{booking.user.name}</p>
  //                 <p className="text-xs text-gray-500 dark:text-gray-400">
  //                   {booking.user.phone_number}
  //                 </p>
  //               </div>
  //               <div className="flex items-center space-x-2">
  //                 <span className={`px-2 py-1 text-xs font-medium rounded-full ${
  //                   booking.bookingStatus === 'Completed'
  //                     ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
  //                     : booking.bookingStatus === 'Pending'
  //                     ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
  //                     : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
  //                 }`}>
  //                   {booking.bookingStatus}
  //                 </span>
  //                 <span className="text-sm font-medium text-gray-900 dark:text-white">{booking.bookingTime}</span>
  //               </div>
  //             </div>
  //             <div className="grid grid-cols-2 gap-2 mt-2">
  //               <p className="text-sm text-gray-600 dark:text-gray-400">
  //                 Tests: {booking.selectedTests.join(', ')}
  //               </p>
  //               <p className="text-sm text-gray-600 dark:text-gray-400">
  //                 Type: {booking.homeCollectionRequired ? 'Home Collection' : 'At Center'}
  //               </p>
  //               <p className="text-sm text-gray-600 dark:text-gray-400">
  //                 Amount: ₹{booking.totalAmount}
  //               </p>
  //               <p className="text-sm text-gray-600 dark:text-gray-400">
  //                 Payment: {booking.paymentStatus}
  //               </p>
  //             </div>
  //           </div>
  //         ))}
  //     </div>
  //   </div>
  // );

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
      {/* Header with Diagnostic Center Info */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
              <BeakerIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{diagnosticCenterData.name}</h1>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{diagnosticCenterData.address}, {diagnosticCenterData.city}, {diagnosticCenterData.state}</span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <PhoneIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{diagnosticCenterData.mainContactNumber}</span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{diagnosticCenterData.email}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center flex-wrap justify-end gap-2">
              <button
                onClick={() => setShowDetails(true)}
                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                title="View Details"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-4 border-t dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                diagnosticCenterData.vendor.status === 'approved'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : diagnosticCenterData.vendor.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {diagnosticCenterData.vendor.status.charAt(0).toUpperCase() + diagnosticCenterData.vendor.status.slice(1)}
                </span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                diagnosticCenterData.vendor.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {diagnosticCenterData.vendor.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleToggleStatus(diagnosticCenterData.vendor.vendorId, diagnosticCenterData.vendor.status)}
                disabled={updatingStatus === diagnosticCenterData.vendor.vendorId}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
                  diagnosticCenterData.vendor.status === 'approved'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {updatingStatus === diagnosticCenterData.vendor.vendorId ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    {diagnosticCenterData.vendor.status === 'approved' ? (
                      <>
                        <ClockIcon className="w-4 h-4" />
                        <span>Mark as Pending</span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-4 h-4" />
                        <span>Mark as Approve</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3">
            <button
              type="button"
              onClick={() => navigate(`/vendors/diagnostic-center/${vendorId}/event-timeline`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RectangleStackIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              Event timeline
            </button>
            <button
              type="button"
              onClick={() => navigate(`/vendors/diagnostic-center/${vendorId}/login-activity`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ShieldCheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              Login activity
            </button>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetails && diagnosticCenterData && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            <div className="flex justify-between items-center border-b dark:border-gray-700 p-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Diagnostic Center Details
                </h3>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  diagnosticCenterData.vendor.status === 'approved'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : diagnosticCenterData.vendor.status === 'pending'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}>
                  {diagnosticCenterData.vendor.status.charAt(0).toUpperCase() + diagnosticCenterData.vendor.status.slice(1)}
                </span>
              </div>
            <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
            >
                <XCircleIcon className="w-5 h-5" />
            </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-4rem)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Basic Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Center Name</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Owner Name</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.ownerName}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">GST Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.gstNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">PAN Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.panNumber}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Contact Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Main Contact</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.mainContactNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Emergency Contact</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.emergencyContactNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Website</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.website}</p>
                    </div>
        </div>
      </div>

                {/* Location Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Location Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Address</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.address}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Location</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.city}, {diagnosticCenterData.state} - {diagnosticCenterData.pincode}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Nearby Landmark</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.nearbyLandmark}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Floor</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.floor}</p>
                    </div>
                  </div>
      </div>

                {/* Services and Facilities */}
            <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Services and Facilities</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Test Types</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {diagnosticCenterData.testTypes.map((type, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Business Timings</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{diagnosticCenterData.businessTimings}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Business Days</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {diagnosticCenterData.businessDays.map((day, index) => (
                          <span key={index} className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">
                            {day}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Facilities</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {diagnosticCenterData.parkingAvailable && (
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
                            Parking Available
                          </span>
                        )}
                        {diagnosticCenterData.wheelchairAccess && (
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
                            Wheelchair Access
                          </span>
                        )}
                        {diagnosticCenterData.liftAccess && (
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
                            Lift Access
                          </span>
                        )}
                        {diagnosticCenterData.ambulanceServiceAvailable && (
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
                            Ambulance Service
                      </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Documents and Photos */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Documents and Photos</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Regulatory Compliance</label>
                      <div className="mt-2">
                        <FilePreview 
                          url={diagnosticCenterData.regulatoryComplianceUrl.url}
                          name={diagnosticCenterData.regulatoryComplianceUrl.name}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Quality Assurance</label>
                      <div className="mt-2">
                        <FilePreview 
                          url={diagnosticCenterData.qualityAssuranceUrl.url}
                          name={diagnosticCenterData.qualityAssuranceUrl.name}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Center Photos</label>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {diagnosticCenterData.filesAndImages.map((photo, index) => (
                          <FilePreview 
                            key={index}
                            url={photo.url}
                            name={photo.name}
                            className="w-full"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Updated Core Metrics */}
      {metrics}

      {/* Financial Overview - Moved above the main content */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Financial Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              ₹{labBookings.reduce((sum, booking) => sum + booking.totalAmount, 0).toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center">
              <CalculatorIcon className="h-5 w-5 text-purple-500 dark:text-purple-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">GST Collected</span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              ₹{labBookings.reduce((sum, booking) => sum + booking.gst, 0).toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center">
              <TagIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Home Collection Charges</span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              ₹{labBookings
                .filter(booking => booking.homeCollectionRequired)
                .reduce((sum, booking) => sum + booking.reportDeliveryFees, 0)
                .toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center">
              <ArrowUturnLeftIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Discounts</span>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              ₹{labBookings.reduce((sum, booking) => sum + booking.discount, 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="space-y-6">
        {/* Bookings List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
          <div className="flex flex-col space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">All Bookings</h2>
              <div className="flex items-center space-x-4">
                {/* Search Bar */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search bookings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
                
                {/* Status Filter */}
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white appearance-none"
                  >
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Accepted">Accepted</option>
                    <option value="Rejected">Rejected</option>
                    <option value="SampleCollected">Sample Collected</option>
                    <option value="SampleProcessing">Sample Processing</option>
                    <option value="ReportGenerating">Report Generating</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                  <FunnelIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>
            </div>

            {/* Bookings Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Patient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tests</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date & Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedBookings.map((booking) => (
                    <tr key={booking.bookingId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{booking.user.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{booking.user.phone_number}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{booking.selectedTests.join(', ')}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{booking.bookingDate}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{booking.bookingTime}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          booking.homeCollectionRequired
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                        }`}>
                          {booking.homeCollectionRequired ? 'Home Collection' : 'At Center'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">₹{booking.totalAmount}</div>
                        {booking.discount > 0 && (
                          <div className="text-xs text-green-600 dark:text-green-400">-₹{booking.discount} off</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          booking.bookingStatus === 'Completed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : booking.bookingStatus === 'Pending'
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                            : booking.bookingStatus === 'Cancelled'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                        }`}>
                          {booking.bookingStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          booking.paymentStatus === 'Paid'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        }`}>
                          {booking.paymentStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, filteredBookings.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredBookings.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => setCurrentPage(index + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === index + 1
                            ? 'z-10 bg-purple-50 dark:bg-purple-900/30 border-purple-500 dark:border-purple-400 text-purple-600 dark:text-purple-300'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
            </div>
          </div>

        {/* Most Popular Tests and Patient Demographics in a grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Most Popular Tests */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Most Popular Tests</h2>
            <div className="space-y-4">
              {[
                { test: 'Complete Blood Count', count: 850, percentage: 35 },
                { test: 'Lipid Profile', count: 720, percentage: 30 },
                { test: 'Thyroid Function', count: 580, percentage: 25 },
                { test: 'Diabetes Screening', count: 420, percentage: 20 },
                { test: 'Liver Function', count: 380, percentage: 15 },
              ].map((item) => (
                <div key={item.test} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.test}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.count} bookings</p>
                  </div>
                  <div className="flex items-center">
                    <div className="w-32 h-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-2">
                      <div 
                        className="h-2 bg-purple-500 rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{item.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Patient Demographics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Patient Demographics</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Age Distribution</h3>
                <div className="space-y-3">
                  {[
                    { range: '0-18', percentage: 15 },
                    { range: '19-35', percentage: 35 },
                    { range: '36-50', percentage: 25 },
                    { range: '51-65', percentage: 15 },
                    { range: '65+', percentage: 10 },
                  ].map((item) => (
                    <div key={item.range} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.range}</span>
                      <div className="flex items-center">
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-2">
                          <div 
                            className="h-2 bg-purple-500 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Test Type Preference</h3>
                <div className="space-y-3">
                  {[
                    { type: 'Home Collection', percentage: 65 },
                    { type: 'At Center', percentage: 35 },
                  ].map((item) => (
                    <div key={item.type} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.type}</span>
                      <div className="flex items-center">
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-2">
                          <div 
                            className="h-2 bg-purple-500 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticCenterVendorDashboard; 